/**
 * Template Review — vocabulary shared by the page, the cards and the preview.
 */

/**
 * Review states, in the order a template moves through them.
 * `approved` is a verdict, not publication: only `published` is public.
 */
export const STATUS_TABS = [
  { key: 'pending', label: 'Pending Review' },
  { key: 'revision_requested', label: 'Revisions' },
  { key: 'approved', label: 'Approved' },
  { key: 'published', label: 'Published' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'draft', label: 'Draft' },
];

export const STATUS_LABEL = Object.fromEntries(STATUS_TABS.map((t) => [t.key, t.label]));

export const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  revision_requested: 'bg-sky-100 text-sky-800',
  approved: 'bg-violet-100 text-violet-800',
  published: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  draft: 'bg-gray-100 text-gray-600',
};

// `all` sends no source filter. `manual` is special-cased by the backend:
// hand-made templates carry no source field at all.
export const SOURCES = [
  { key: 'all', label: 'All sources' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'imported', label: 'Imported' },
  { key: 'ai_generated', label: 'AI Generated' },
  { key: 'manual', label: 'Manual' },
  { key: 'marketplace', label: 'Marketplace' },
];

export const FORMATS = [
  { key: '', label: 'All formats' },
  { key: 'us-letter', label: 'US Letter' },
  { key: 'square', label: 'Square' },
  { key: 'portrait-4x5', label: 'Portrait 4:5' },
  { key: 'story', label: 'Story' },
];

export const SORTS = [
  { key: 'newest', label: 'Newest first' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'category', label: 'Category' },
  { key: 'name', label: 'Name A–Z' },
  { key: 'name_desc', label: 'Name Z–A' },
  { key: 'updated', label: 'Recently updated' },
];

export const PAGE_SIZES = [30, 60, 120, 200];

/** Grid density → Tailwind column classes. `large` is for judging a design, not scanning. */
export const DENSITIES = {
  compact: { label: 'Compact', grid: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8' },
  comfortable: { label: 'Comfortable', grid: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' },
  large: { label: 'Large', grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' },
};

/** What each review action is allowed from, so buttons only appear where they do something. */
export const canApprove = (status) => ['pending', 'revision_requested', 'rejected', 'draft'].includes(status);
export const canReject = (status) => ['pending', 'revision_requested', 'approved', 'draft'].includes(status);
export const canRevise = (status) => ['pending', 'approved'].includes(status);
export const canPublish = (status) => status === 'approved';
export const canUnpublish = (status) => status === 'published';

export const pagesOf = (t) => t?.template_json?.pages || [];
export const elementsOf = (t) => pagesOf(t).flatMap((p) => p.elements || []);

export const canvasOf = (t) => ({
  width: t?.canvas_width || t?.template_json?.canvas_width || t?.template_json?.canvasSize?.width || 1080,
  height: t?.canvas_height || t?.template_json?.canvas_height || t?.template_json?.canvasSize?.height || 1080,
});

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
